/**
 * AnimalHistoricoRepository - Repositorio unificado para Historial de Animales
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import {
  IAnimalHistoricoRepository,
  FiltroAnimalHistorico,
  AnimalHistoricoPaginatedResult
} from '../interfaces/IAnimalHistoricoRepository';
import { AnimalHistorico } from '../interfaces/entities';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';

/**
 * Repositorio unificado de Historial de Animales
 */
export class AnimalHistoricoRepository extends BaseRepository<AnimalHistorico, string> implements IAnimalHistoricoRepository {

  constructor(db: IDatabaseAdapter) {
    super(db, 'animales_historico', 'animales_historico');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
  }

  async add(data: Omit<AnimalHistorico, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();

    const record: AnimalHistorico = {
      ...data,
      id,
      fecha_de_creacion: data.fecha_de_creacion || now
    };

    await this.db.insert('animales_historico', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<AnimalHistorico> & { id: string }): Promise<void> {
    await this.db.update('animales_historico', data.id, data);
    this.emitSync('UPDATE', data as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('animales_historico', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Soft Delete ====================
  // Estos métodos ahora vienen de BaseRepository (soporta soft delete habilitado)

  async getByFiltros(filtros: FiltroAnimalHistorico): Promise<AnimalHistorico[]> {
    const caravanasSet = filtros.caravanas && filtros.caravanas.length > 0
      ? new Set(filtros.caravanas)
      : null;

    // Partimos de los registros de la empresa activa para no mezclar datos
    // de otras empresas.
    let results = (await this.getAllForActiveEmpresa()).filter(h => !h.is_deleted);

    if (filtros.estado) {
      results = results.filter(h => h.estado === filtros.estado);
    }
    if (filtros.empresa_id) {
      results = results.filter(h => h.empresa_id === filtros.empresa_id);
    }

    // Filtros de caravana / rango de fechas
    if (caravanasSet || filtros.caravana || filtros.fecha_inicio || filtros.fecha_fin) {
      results = results.filter(h => {
        if (caravanasSet) {
          if (!caravanasSet.has(h.caravana)) return false;
        } else if (filtros.caravana && h.caravana !== filtros.caravana) {
          return false;
        }
        if (filtros.fecha_inicio && h.fecha_de_evento < filtros.fecha_inicio) return false;
        if (filtros.fecha_fin && h.fecha_de_evento > filtros.fecha_fin) return false;
        return true;
      });
    }

    return results;
  }

  async getByCaravana(caravana: string): Promise<AnimalHistorico[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(h => h.caravana === caravana && !h.is_deleted);
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<AnimalHistorico[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(h => {
      if (h.is_deleted) return false;
      return h.fecha_de_evento >= fechaInicio && h.fecha_de_evento <= fechaFin;
    });
  }

  async getByEstado(estado: string): Promise<AnimalHistorico[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(h => h.estado === estado && !h.is_deleted);
  }

  async getByEmpresa(empresaId: string): Promise<AnimalHistorico[]> {
    return this.db.queryMultiple<AnimalHistorico>('animales_historico', [
      { field: 'empresa_id', operator: 'equals', value: empresaId },
      { field: 'is_deleted', operator: 'equals', value: false }
    ]);
  }

  async getAllPaginated(
    offset: number,
    limit: number,
    filtros?: FiltroAnimalHistorico,
    sortBy: string = 'fecha_de_creacion',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<AnimalHistoricoPaginatedResult> {
    let items = filtros ? await this.getByFiltros(filtros) : await this.getAllForActiveEmpresa();

    // Sort
    items.sort((a, b) => {
      const aVal = (a as any)[sortBy] || '';
      const bVal = (b as any)[sortBy] || '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    const total = items.length;
    items = items.slice(offset, offset + limit);

    return {
      items,
      total,
      hasMore: offset + limit < total
    };
  }

  async getUltimoEvento(caravana: string): Promise<AnimalHistorico | undefined> {
    const eventos = await this.getByCaravana(caravana);
    if (eventos.length === 0) return undefined;

    return eventos.reduce((latest, current) => {
      if (!latest.fecha_de_creacion) return current;
      if (!current.fecha_de_creacion) return latest;
      return current.fecha_de_creacion > latest.fecha_de_creacion ? current : latest;
    });
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================
}
